import React from 'react';
import ExcelJS from 'exceljs';
import { translate } from '../../services/translate';
import { Inscription } from '../../typings/third-party-approved-companies';
import Toast from 'react-native-toast-message';
import { InscriptionStatus, thirdPartyApprovedCompaniesService } from '../../services/central-api/third-party-approved-companies';
import { StyleProp, View, ViewStyle } from 'react-native';
import Badge, { BadgeColor } from '../../components/Badge';

export const validUfs = [
    'ac',
    'al',
    'am',
    'ap',
    'ba',
    'ce',
    'df',
    'es',
    'go',
    'ma',
    'mg',
    'ms',
    'mt',
    'pa',
    'pb',
    'pe',
    'pi',
    'pr',
    'rj',
    'rn',
    'ro',
    'rr',
    'rs',
    'sc',
    'se',
    'sp',
    'to'];

function getMimeTypeFromFileName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
        case 'pdf':
            return 'application/pdf';
        case 'png':
            return 'image/png';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        default:
            return 'application/octet-stream';
    }
}

export async function createFileFromUrl(url: string, fileName: string): Promise<File> {
    const response = await fetch(url);
    const blob = await response.blob();

    const mimeType = response.headers.get('Content-Type')?.split(';')[0] || getMimeTypeFromFileName(fileName);

    return new File([blob], fileName, { type: mimeType });
}

function generateUniqueSheetName(workbookNames: string[], rawName: string) {
    const sanitized = rawName.replace(/[*?:\\/[\]]/g, '').replace(/\s+/g, '_').trim();
    const MAX_LENGTH = 31;

    let counter = 0;
    let finalName = sanitized.slice(0, MAX_LENGTH);

    for (const workbookName of workbookNames) {
        if (workbookName == finalName) {
            counter++;
            const suffix = `_${counter}`;
            finalName = sanitized.slice(0, MAX_LENGTH - suffix.length) + suffix;
        }
    }
    return finalName;
}

export async function generateInscriptionCsv(inscriptionsIds: string[]) {
    try {
        let inscriptions: Inscription[] = [];
        try {
            inscriptions = await thirdPartyApprovedCompaniesService.getMultipleInscription({ ids: inscriptionsIds });
        } catch (err) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });

            return;
        }

        const fileName = inscriptions.length === 1
            ? `${inscriptions[0].name.replace(' ', '_')}-${inscriptions[0].companyName.replace(/[*?\\/:[\]]/g, '').replace(' ', '_')}`
            : 'Inscrições';

        const workbookNames: string[] = [];
        const workbook = new ExcelJS.Workbook();
        for (const item of inscriptions) {
            const sheetName = generateUniqueSheetName(workbookNames, item.companyName);
            workbookNames.push(sheetName);
            const sheet = workbook.addWorksheet(sheetName);

            sheet.addRow([translate('status'), translate(getInscriptionStatusLabel(item.status))]).commit();
            sheet.addRow([]).commit();

            sheet.addRow([translate('registrationOfficerData')]).commit();
            sheet.addRow([]).commit();
            sheet.addRow([translate('fullName'), 'CPF', translate('contactPhoneNumber'), translate('roleInCompany')]).commit();
            sheet.addRow([item.name, item.document, item.phone, item.role]).commit();
            sheet.addRow([]).commit();

            sheet.addRow([translate('companyData')]).commit();
            sheet.addRow([]).commit();
            sheet.addRow([translate('companyName'), translate('tradeName'), 'CNPJ', translate('contactPhoneNumber')]).commit();
            sheet.addRow([item.companyName, item.tradeName, item.cnpj, item.commercialPhone]).commit();
            sheet.addRow([translate('corporateEmail'), translate('companyType'), translate('companyWebsite')]).commit();
            sheet.addRow([item.businessEmail, item.companyType, item.companyWebsite]).commit();
            sheet.addRow([]).commit();

            sheet.addRow([translate('addressData')]).commit();
            sheet.addRow([]).commit();
            sheet.addRow([translate('cep'), translate('street'), translate('number'), translate('city'), translate('neighborhood'), translate('uf')]).commit();
            sheet.addRow([item.cep, item.address, item.number, item.city, item.neighborhood, item.state]).commit();
            sheet.addRow([]).commit();

            sheet.addRow([translate('integratedCamerasQuantity')]).commit();
            sheet.addRow([]).commit();
            sheet.addRow([translate('camerasQuantity')]).commit();
            sheet.addRow([item.integratedCamerasQty]).commit();

            sheet.addRow([translate('visualIdentity')]).commit();
            sheet.addRow([]).commit();

            let fileExt = item.files.visualIdentity.fileName.split('.').pop() || '';
            if (fileExt == 'jpg') {
                fileExt = 'jpeg';
            }

            sheet.getRow(26).height = 120;
            sheet.getColumn(1).width = 34;
            if (['jpeg', 'png'].includes(fileExt)) {
                const file = await createFileFromUrl(item.files.visualIdentity.fileUrl, item.files.visualIdentity.fileName);
                const arrayBuffer = await file.arrayBuffer();

                const imageId = workbook.addImage({
                    buffer: arrayBuffer,
                    extension: fileExt as ('jpeg' | 'png')
                });
                sheet.addImage(imageId, {
                    tl: { col: 0, row: 25 },
                    ext: { width: 240, height: 120 },
                    editAs: 'oneCell'
                });
            } else {
                sheet.getCell('A26').value = item.files.visualIdentity.fileName;
            }
            sheet.getCell('B26').value = item.files.visualIdentity.accepted ? translate('approved_singular_m') : translate('denied');
            sheet.addRow([]).commit();

            sheet.addRow([translate('requiredAttachments')]).commit();
            sheet.addRow([]).commit();
            sheet.addRow([translate('legalEntityRegistrationProofAndStatus')]).commit();
            sheet.addRow([item.files.legalEntityRegistrationProofAndStatus.fileName, item.files.legalEntityRegistrationProofAndStatus.accepted ? translate('approved_singular_m') : translate('denied')]).commit();

            sheet.addRow([]).commit();
            sheet.addRow([translate('companyAdministratorDocument')]).commit();
            sheet.addRow([item.files.companyAdministratorDocument.fileName, item.files.companyAdministratorDocument.accepted ? translate('approved_singular_m') : translate('denied')]).commit();

            sheet.addRow([]).commit();
            sheet.addRow([translate('socialContract')]).commit();
            sheet.addRow([item.files.socialContract.fileName, item.files.socialContract.accepted ? translate('approved_singular_m') : translate('denied')]).commit();

            sheet.addRow([]).commit();
            sheet.addRow([translate('assignmentAgreement')]).commit();
            sheet.addRow([item.files.assignmentAgreement.fileName, item.files.assignmentAgreement.accepted ? translate('approved_singular_m') : translate('denied')]).commit();
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${fileName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
    } catch (err) {
        console.error(err);
        Toast.show({
            type: 'sentinelxError',
            text1: translate('unexpectedError'),
        });
    }
}

export function getInscriptionStatus(params: { status: InscriptionStatus; disabled?: boolean; style?: StyleProp<ViewStyle>; }) {
    let customName = 'pending';
    let badgeColor: BadgeColor = 'orange';

    if (params.status === 'failed') {
        customName = 'failed_test';
        badgeColor = 'red';
    } else if (params.status === 'approved') {
        customName = 'approved_singular';
        badgeColor = 'green';
    } else if (params.status === 'irregular') {
        customName = 'irregular';
        badgeColor = 'purple';
    } else if (params.status === 'published') {
        customName = 'published_f';
        badgeColor = 'gray';
    } else if (params.status === 'waiting') {
        customName = 'waiting';
        badgeColor = 'blue';
    }

    return <View style={[{ justifyContent: 'center' }, params.style]}>
        <Badge style={{ opacity: params.disabled ? 0.75 : 1 }} mode='SQUARE' size='medium' color={badgeColor}>{translate(customName)}</Badge>
    </View>;
}

export function getInscriptionStatusLabel(status: InscriptionStatus) {
    switch (status) {
        case 'approved': return 'approved_singular';
        case 'pending': return 'pending';
        case 'waiting': return 'waiting';
        case 'irregular': return 'irregular';
        case 'failed': return 'failed_test';
        case 'published': return 'published_f';
    }
}

export function getInscriptionStatusBgColor(status: InscriptionStatus) {
    switch (status) {
        case 'approved': return '#CCE1CF';
        case 'pending': return '#F0D1AC';
        case 'waiting': return '#C4D0E9';
        case 'irregular': return '#D6C4E9';
        case 'failed': return '#EDC9C7';
        case 'published': return '#696969';
    }
}

export function getInscriptionStatusTextColor(status: InscriptionStatus) {
    switch (status) {
        case 'approved': return '#24622E';
        case 'pending': return '#8B4900';
        case 'waiting': return '#294278';
        case 'irregular': return '#552D7F';
        case 'failed': return '#60332E';
        case 'published': return '#FFFFFF';
    }
}
